import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { clarity } from '@/utils/clarity';
import { ProHero } from '@/components/landing/pro/ProHero';
import { ProTargets } from '@/components/landing/pro/ProTargets';
import { ProDifferentiation } from '@/components/landing/pro/ProDifferentiation';
import { ProScenarios } from '@/components/landing/pro/ProScenarios';
import { ProWhiteLabel } from '@/components/landing/pro/ProWhiteLabel';
import { ProPricing } from '@/components/landing/pro/ProPricing';
import { ProFAQ } from '@/components/landing/pro/ProFAQ';
import { ProCTA } from '@/components/landing/pro/ProCTA';

export const ProHomePage: React.FC = () => {
  useEffect(() => {
    clarity.setTag('page_type', 'landing_pro');
  }, []);

  return (
    <>
      <Helmet>
        <title>DPEWizard Pro - Conseiller vos clients avec précision sur n'importe quel DPE</title>
        <meta name="description" content="DPEWizard Pro analyse le DPE de votre client, contextualise son bien et génère automatiquement les scénarios de travaux les plus pertinents. Pour courtiers en travaux, agents immobiliers et investisseurs." />
        <meta name="keywords" content="DPE professionnel, analyse DPE courtier, scénarios travaux agent immobilier, outil DPE investisseur, rapport PDF DPE, marque blanche DPE" />

        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://pro.dpewizard.com/" />
        <meta property="og:title" content="DPEWizard Pro - Conseiller vos clients avec précision sur n'importe quel DPE" />
        <meta property="og:description" content="DPEWizard Pro analyse le DPE de votre client, contextualise son bien et génère automatiquement les scénarios de travaux les plus pertinents." />
        <meta property="og:image" content="https://pro.dpewizard.com/og-image.jpg" />

        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://pro.dpewizard.com/" />
        <meta property="twitter:title" content="DPEWizard Pro - Conseiller vos clients avec précision sur n'importe quel DPE" />
        <meta property="twitter:description" content="DPEWizard Pro analyse le DPE de votre client, contextualise son bien et génère automatiquement les scénarios de travaux les plus pertinents." />
        <meta property="twitter:image" content="https://pro.dpewizard.com/twitter-image.jpg" />

        <link rel="canonical" href="https://pro.dpewizard.com/" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "DPEWizard Pro",
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Web",
            "offers": [
              {
                "@type": "Offer",
                "price": "19.90",
                "priceCurrency": "EUR",
                "name": "Unlimited Power - Mensuel",
                "priceValidUntil": "2026-12-31"
              },
              {
                "@type": "Offer",
                "price": "99.00",
                "priceCurrency": "EUR",
                "name": "Unlimited Pro Agence - Mensuel",
                "priceValidUntil": "2026-12-31"
              }
            ],
            "description": "DPEWizard Pro analyse le DPE de votre client, contextualise son bien et génère automatiquement les scénarios de travaux les plus pertinents. Pour courtiers en travaux, agents immobiliers et investisseurs.",
            "audience": {
              "@type": "BusinessAudience",
              "audienceType": "Professionnels de l'immobilier et de la rénovation"
            }
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Les résultats sont-ils fiables ?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "DPEWizard récupère les données officielles ADEME et les enrichit avec un questionnaire contextuel pour affiner les recommandations et les estimations de coût. Les simulations restent des estimations, comme tout DPE projeté. Pour l'usage qui est le vôtre, conseiller un client en rendez-vous ou évaluer une opération, ce niveau de précision est largement suffisant."
                }
              },
              {
                "@type": "Question",
                "name": "Faut-il être expert en rénovation pour utiliser l'outil ?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Non. Le questionnaire est conçu pour être accessible à n'importe quel professionnel, sans connaissance technique en travaux. Les questions sont simples, les réponses sont guidées. Toute la complexité est absorbée par l'outil."
                }
              },
              {
                "@type": "Question",
                "name": "Les rapports PDF peuvent-ils être à mon nom ?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Oui, avec l'offre Unlimited Power Pro. Vos rapports sortent avec votre logo et le nom de votre entreprise. Vous les remettez directement à vos clients."
                }
              },
              {
                "@type": "Question",
                "name": "Combien de temps prend une analyse complète ?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Entre 2 et 5 minutes selon le bien et les réponses au questionnaire."
                }
              },
              {
                "@type": "Question",
                "name": "Peut-on utiliser l'outil directement en rendez-vous client ?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Oui, c'est précisément pour ça qu'il a été conçu. L'analyse se fait en temps réel, le rapport est généré immédiatement et peut être remis ou envoyé au client avant la fin du rendez-vous."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      <div className="min-h-screen">
        <ProHero />
        <ProTargets />
        <ProDifferentiation />
        <ProScenarios />
        <ProWhiteLabel />
        <ProPricing />
        <ProFAQ />
        <ProCTA />
      </div>
    </>
  );
};

export default ProHomePage;
